import { useCallback, useRef, useState } from "react"
import { Icon, Button, MenuItem, Menu, Tooltip, ListItemIcon, ListItemText } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { ECounterType, PartsBundleParams } from "@tm/models"
import { TmaHelper } from "@tm/utils"
import Morpheus from "@tm/morpheus"
import { useOwnListsCompilationsParameters } from "@tm/context-distribution"
import { Link } from "react-router-dom"
import { useFavoriteArticlesStore } from "@bundles/compilations/components/compilations/FavoriteArticles/store"
import { useOwnArticlesStore } from "@bundles/compilations/components/compilations/OwnArticles/store"

type Props = {
    vehicleId?: string
    partsDirectListUrl: string
    partsVehicleListUrl: string
    onCreateCustomArticle(): void
    onOpenFavoriteArticles(): void
    onOpenOwnArticles(): void
}

export default function AddPart(props: Props) {
    const { partsVehicleListUrl, partsDirectListUrl, vehicleId, onCreateCustomArticle, onOpenFavoriteArticles, onOpenOwnArticles } = props
    const { translateText } = useLocalization()
    const { hasMyArticles, hasFavoriteArticles } = useOwnListsCompilationsParameters()
    const { setQuery } = useFavoriteArticlesStore()
    const { setSearchQuery } = useOwnArticlesStore()
    const { disableArticleListAccess } = Morpheus.getParams<PartsBundleParams>("parts")
    const buttonRef = useRef<HTMLButtonElement>(null)

    const [openMenu, setOpenMenu] = useState<boolean>(false)

    const handleOpenMenu = useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e?.stopPropagation?.()
            TmaHelper.GeneralCountEvent.Call(ECounterType.CostEstimateAddArticle)
            setOpenMenu(!openMenu)
        },
        [openMenu]
    )

    const handleCreateClick = useCallback((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e?.stopPropagation?.()
        onCreateCustomArticle()
        setOpenMenu(false)
    }, [])

    const handleOpenFavoriteClick = useCallback((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e?.stopPropagation?.()
        onOpenFavoriteArticles()
        setOpenMenu(false)
        setQuery("")
    }, [])

    const handleOpenOwnArticlesClick = useCallback((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e?.stopPropagation?.()
        onOpenOwnArticles()
        setOpenMenu(false)
        setSearchQuery("")
    }, [])

    return (
        <>
            <Button onClick={handleOpenMenu} ref={buttonRef} startIcon={<Icon name="plus" />} variant="contained" color="highlight" />
            <Menu
                open={openMenu}
                onClose={() => setOpenMenu(false)}
                anchorEl={buttonRef.current}
                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            >
                <MenuItem onClick={handleCreateClick}>
                    <ListItemIcon>
                        <Icon name="add" />
                    </ListItemIcon>
                    <ListItemText>
                        {translateText(13906)}
                        <Tooltip title={translateText(13908)}>
                            <Icon name="info" />
                        </Tooltip>
                    </ListItemText>
                </MenuItem>
                {hasFavoriteArticles && (
                    <MenuItem onClick={handleOpenFavoriteClick}>
                        <ListItemIcon>
                            <Icon name="vote-star" />
                        </ListItemIcon>
                        <ListItemText>{translateText(13905)}</ListItemText>
                    </MenuItem>
                )}
                {hasMyArticles && (
                    <MenuItem onClick={handleOpenOwnArticlesClick}>
                        <ListItemIcon>
                            <Icon name="individual-article" />
                        </ListItemIcon>
                        <ListItemText>{translateText(757)}</ListItemText>
                    </MenuItem>
                )}
                {!disableArticleListAccess && (
                    <MenuItem<Link> component={Link} to={vehicleId ? partsVehicleListUrl : partsDirectListUrl}>
                        <ListItemIcon>
                            <Icon name="catalog" />
                        </ListItemIcon>
                        <ListItemText>{translateText(136)}</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    )
}
